import type { Json } from '@/lib/database.types';
import { groupBySection, NA, type SurveyAnswers, type SurveyQuestion } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * שאלות הסקר כפי שהעובד רואה אותן.
 *
 * בקובץ משלו כדי שהתצוגה המקדימה בבונה תרנדר בדיוק את אותו קוד.
 * תצוגה מקדימה שמחזיקה עותק משלה היא תצוגה שמשקרת: היא מתיישנת
 * ברגע שמשנים כאן משהו, ואז מה שראית אינו מה שנשלח.
 *
 * החלוקה לחלקים נותנת קצב — סקר של עשרים שאלות ברשימה אחת נראה
 * אינסופי, ומי שגולל בו נוטש באמצע.
 */
export function SurveyQuestions({
  questions,
  answers,
  onChange,
}: {
  questions: SurveyQuestion[];
  answers: SurveyAnswers;
  onChange: (questionId: string, value: Json) => void;
}) {
  return (
    <>
      {groupBySection(questions).map((group, index) => (
        <section key={group.section ?? index} className="grid gap-3">
          {group.section && (
            <h3 className="m-0 border-b border-line pb-1 text-title font-semibold text-ink">
              {group.section}
            </h3>
          )}
          {group.questions.map((question) => (
            <Question
              key={question.id}
              question={question}
              value={answers[question.id]}
              onChange={(value) => onChange(question.id, value)}
            />
          ))}
        </section>
      ))}
    </>
  );
}

function Question({
  question,
  value,
  onChange,
}: {
  question: SurveyQuestion;
  value: Json | undefined;
  onChange: (value: Json) => void;
}) {
  const options = Array.isArray(question.options) ? question.options : [];

  return (
    <fieldset className="m-0 grid gap-2 border-0 p-0">
      <legend className="mb-1 p-0 text-body font-medium">
        {question.prompt}
        {!question.is_required && <span className="ms-1 font-normal text-muted">לא חובה</span>}
      </legend>

      {question.answer_kind === 'text' && (
        <textarea
          rows={3}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
          aria-label={question.prompt}
          className={`${FIELD} py-2`}
        />
      )}

      {question.answer_kind === 'scale' && (
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map((score) => (
            <button
              key={score}
              type="button"
              onClick={() => onChange(score)}
              aria-pressed={value === score}
              className={`min-h-11 min-w-11 cursor-pointer rounded-sm border text-body font-medium ${
                value === score
                  ? 'border-charcoal bg-charcoal text-on-charcoal'
                  : 'border-muted bg-surface text-ink'
              }`}
            >
              {score}
            </button>
          ))}

          {/*
            בלי המוצא הזה מי שלא השתמש בפיצ'ר מדרג אותו בכל זאת,
            והממוצע מודד ניחושים. התשובה נספרת בנפרד ואינה נכנסת
            לממוצע (מיגרציה 085).
          */}
          {question.allow_na && (
            <button
              type="button"
              onClick={() => onChange(NA)}
              aria-pressed={value === NA}
              className={`min-h-11 cursor-pointer rounded-sm border px-3 text-body font-medium ${
                value === NA
                  ? 'border-charcoal bg-charcoal text-on-charcoal'
                  : 'border-muted bg-surface text-ink'
              }`}
            >
              לא רלוונטי
            </button>
          )}
        </div>
      )}

      {question.answer_kind === 'choice' &&
        options.map((option) => (
          <label key={option} className="flex items-center gap-2 text-body">
            <input
              type="radio"
              name={question.id}
              checked={value === option}
              onChange={() => onChange(option)}
              className="size-5"
            />
            {option}
          </label>
        ))}

      {question.answer_kind === 'multi' &&
        options.map((option) => {
          const selected = Array.isArray(value) ? (value as string[]) : [];
          return (
            <label key={option} className="flex items-center gap-2 text-body">
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option]
                      : selected.filter((item) => item !== option),
                  )
                }
                className="size-5"
              />
              {option}
            </label>
          );
        })}
    </fieldset>
  );
}
